/**
 * PreSet 문서 — 서명자 중복 제거 (CONTACT_CD / REG_NO / 이름 교차 매칭).
 * AGM Attendance, Consent, Allotment 등 자동 서명자 삽입 시 공통 사용.
 */
import type { PresetDocCustSigner } from './presetDocCustSave';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

export function normalizeSignerNameForDedup(name: string): string {
  return String(name || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

export function normalizeContactRegNoForDedup(regNo: string): string {
  return String(regNo || '').replace(/[\s-]/g, '').toUpperCase();
}

export interface PresetSignerDedupRow {
  name: string;
  email?: string;
  role?: string;
  contactCd?: string;
  contactRegNo?: string;
  title?: string;
}

const ROLE_PRIORITY: Record<string, number> = {
  director: 4,
  chairman: 3,
  shareholder: 2,
  member: 2,
  signer: 1,
};

function rolePriority(role: string | undefined): number {
  return ROLE_PRIORITY[String(role || '').toLowerCase()] ?? 1;
}

function pickHigherRole(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return rolePriority(b) > rolePriority(a) ? b : a;
}

function mergePresetSignerRows(
  prev: PresetSignerDedupRow,
  next: PresetSignerDedupRow,
): PresetSignerDedupRow {
  return {
    name: trim(prev.name) || trim(next.name),
    email: trim(prev.email) || trim(next.email),
    role: pickHigherRole(prev.role, next.role),
    contactCd: trim(prev.contactCd) || trim(next.contactCd),
    contactRegNo: trim(prev.contactRegNo) || trim(next.contactRegNo),
    title: trim(prev.title) || trim(next.title),
  };
}

function primaryKeyForRow(row: PresetSignerDedupRow): string {
  const cd = trim(row.contactCd);
  if (cd) return `cd:${cd.toUpperCase()}`;
  const reg = normalizeContactRegNoForDedup(row.contactRegNo || '');
  if (reg) return `reg:${reg}`;
  return `nm:${normalizeSignerNameForDedup(row.name)}`;
}

export interface DedupePresetSignersOptions {
  /** chairman 과 member/director 동일인이면 1명으로 유지 (별도 chairman 행 추가 안 함) */
  chairmanName?: string;
  defaultRole?: string;
}

/**
 * CONTACT_CD·REG_NO·이름(교차) 기준 1인 1회.
 * 동일인이 shareholder + director 등 다른 role 로 들어오면 director 우선.
 */
export function dedupePresetSigners(
  rows: PresetSignerDedupRow[],
  options?: DedupePresetSignersOptions,
): PresetDocCustSigner[] {
  const merged = new Map<string, PresetSignerDedupRow>();
  const nameToKey = new Map<string, string>();

  for (const raw of rows) {
    const name = trim(raw.name);
    if (!name) continue;
    const normName = normalizeSignerNameForDedup(name);
    const row: PresetSignerDedupRow = { ...raw, name };

    const existingKeyByName = nameToKey.get(normName);
    if (existingKeyByName && merged.has(existingKeyByName)) {
      merged.set(existingKeyByName, mergePresetSignerRows(merged.get(existingKeyByName)!, row));
      continue;
    }

    const key = primaryKeyForRow(row);
    const prev = merged.get(key);
    if (!prev) {
      merged.set(key, row);
      nameToKey.set(normName, key);
      continue;
    }

    const mergedRow = mergePresetSignerRows(prev, row);
    merged.set(key, mergedRow);
    nameToKey.set(normalizeSignerNameForDedup(mergedRow.name), key);
  }

  const defaultRole = options?.defaultRole || 'signer';

  return Array.from(merged.values()).map((row) => ({
    name: trim(row.name),
    email: trim(row.email),
    role: trim(row.role) || defaultRole,
  }));
}
